import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import className from "classnames";
import styles from "./styles/AccordionTerrace.module.scss";
import editStyles from "../../SchoolBlocks/styles/EditPane.module.scss";
import { RequiredInput } from "../../SchoolBlocks/EditBlockComponents/EditBlockInputs/RequiredInput";
import MarkdownEditor from "../../../editors/MarkdownEditor";
import WatsonApi from "../../../../backends/WatsonApi";
import { runInAction } from "mobx";
import NotificationManager from "../../../notifications/NotificationManager";
import { useStores } from "../../../../stores/useStores";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import SchoolBlocksLoadingIndicator from "../../../utilities/SchoolBlocksLoadingIndicator";
import FontAwesome from "../../../utilities/FontAwesome";
import { ContactSupport } from "../../../misc/ContactSupport";
import NewTag from "../../../admin/sidebar/items/NewTag";
import ClickableLink from "../../../utilities/ClickableLink";
import { HtmlEditor, serializeSlateToHtml } from "../../../editors/HtmlEditor";
import type { SlateEditor } from "platejs";
import { NodeType } from "../../../editors/utils";
import { PlaceholderPlugin } from "@platejs/media/react";
import { compressImage } from "../../../../utils/DataUtilities";
import { getCdnUrl } from "../../../../utils/SchoolBlocksUtilities";
import { useMachine } from "@xstate/react";
import { FetchMachine } from "../../../machines/FetchMachine";

const AccordionItemEditor = observer(
  (
    props: {
      blockObj: IAccordionTerrace;
      index: number;
      handleSave?: () => void;
      handleCancel: () => void;
      accordionList: IAccordionItem[];
    } & (
      | {
          item: IAccordionItem;
          parentItem?: never;
        }
      | {
          item?: never;
          parentItem: IAccordionItem;
        }
      | {
          item?: never;
          parentItem?: never;
        }
    ),
  ) => {
    const { organizationStore, interfaceStore } = useStores();

    const [title, setTitle] = useState(props?.item?.title || "");
    const [summary, setSummary] = useState<string | null>(
      props.item ? null : "",
    );
    const [content, setContent] = useState("");
    const [addContent, setAddContent] = useState(props?.item?.has_content);
    const [current, send] = useMachine(FetchMachine);
    const htmlEditorRef = useRef<{ editor: SlateEditor }>(null);
    const htmlId = `accordion-item-editor${props.parentItem ? "-" + props.parentItem.id : ""}${props.item ? "-" + props.item.id : ""}-${props.index}`;
    const hasDocmersion =
      organizationStore.organization.billing_products.docmersion;

    const handleSave = async () => {
      send("FETCH");
      try {
        const client = await WatsonApi();
        let html;

        if (addContent) {
          const editor = htmlEditorRef.current?.editor;
          if (!editor) return;

          const mediaPlaceholders = editor.api.nodes({
            match: { type: NodeType.MEDIA_PLACEHOLDER },
            at: [],
          });
          for (let placeholder of mediaPlaceholders) {
            const placeholderApi = editor.getApi(PlaceholderPlugin);
            const placeholderId = placeholder[0].id as string;
            const file =
              placeholderApi.placeholder.getUploadingFile(placeholderId);
            const compressedFile = await compressImage(file, 2);

            const response =
              await client.apis.organizations.organizations_terraces_upload_image(
                {
                  organization_pk: props.blockObj.organization_id,
                  id: props.blockObj.id,
                  file: compressedFile,
                },
              );
            const imageUrl = response.body.url;
            editor.tf.setNodes(
              {
                type: NodeType.IMAGE,
                url: getCdnUrl(imageUrl),
              },
              {
                at: placeholder[1],
              },
            );
          }

          html = await serializeSlateToHtml(editor);
        }

        let response;
        if (props.item) {
          response =
            await client.apis.organizations.organizations_terraces_accordion_items_partial_update(
              {
                organization_pk: props.blockObj.organization_id,
                terrace_pk: props.blockObj.id,
                id: (props.item as IAccordionItem).id,
                data: {
                  title,
                  description: summary,
                  content_html: html,
                },
              },
            );
        } else {
          let path = "";

          if (props.parentItem && props.blockObj.items) {
            const parentPath = props.parentItem.path;
            const siblings = props.blockObj.items.filter((item) => {
              return (
                item.path &&
                item.path.startsWith(parentPath + ".") &&
                item.path.split(".").length === parentPath.split(".").length + 1
              );
            });
            let lastSiblingNum = 0;
            if (siblings.length > 0) {
              const lastSiblingPathParts =
                siblings[siblings.length - 1].path.split(".");
              lastSiblingNum = parseInt(
                lastSiblingPathParts[lastSiblingPathParts.length - 1],
              );
            }

            const newChildIndex = lastSiblingNum + 1;
            const paddedIndex = String(newChildIndex).padStart(3, "0");
            path = `${parentPath}.${paddedIndex}`;
          } else {
            let lastItemNum = 0;
            if ((props.blockObj.items as IAccordionItem[]).length > 0) {
              const lastItemPath = (props.blockObj.items as IAccordionItem[])[
                (props.blockObj.items as IAccordionItem[]).length - 1
              ].path.split(".")[0];
              lastItemNum = parseInt(
                lastItemPath.slice(
                  lastItemPath.length - 3,
                  lastItemPath.length,
                ),
              );
            }
            path = `${(lastItemNum + 1).toString().padStart(3, "0")}`;
          }

          response =
            await client.apis.organizations.organizations_terraces_accordion_items_create(
              {
                organization_pk: props.blockObj.organization_id,
                terrace_pk: props.blockObj.id,
                data: {
                  title,
                  description: summary,
                  content_html: html,
                  path,
                },
              },
            );
        }

        runInAction(() => {
          const updatedItem = JSON.parse(response.data);
          if (props.item) {
            props.blockObj.items = (
              props.blockObj.items as IAccordionItem[]
            ).map((item) => {
              return item.id === (props.item as IAccordionItem).id
                ? updatedItem
                : item;
            });
          } else {
            (props.blockObj.items as IAccordionItem[]).push(updatedItem);
            props.blockObj.items.sort((a, b) => a.path.localeCompare(b.path));
          }
        });
        NotificationManager.success("Terrace successfully saved!");
        send("SUCCESS");
        interfaceStore.scrollToElement(`#${htmlId}`, { behavior: "instant" });
        props.handleCancel();
      } catch (e) {
        console.error(e);
        NotificationManager.error("Something went wrong! Please try again.");
        send("FAILURE");
      }
    };

    const titleIsValid = title && title?.length > 0;

    const markdownEditorClassName = className({
      [styles.markdownEditor]: true,
      [styles.markdownEditorContent]: true,
    });

    const handleSubmit = async () => {
      await handleSave();
    };

    const handleDelete = async () => {
      try {
        const client = await WatsonApi();
        await client.apis.organizations.organizations_terraces_accordion_items_delete(
          {
            organization_pk: props.blockObj.organization_id,
            terrace_pk: props.blockObj.id,
            id: (props.item as IAccordionItem).id,
          },
        );
        runInAction(() => {
          props.blockObj.items = (
            props.blockObj.items as IAccordionItem[]
          ).filter((item) => {
            return item.id !== (props.item as IAccordionItem).id;
          });
        });
        NotificationManager.success("Accordion Item deleted.");
      } catch (e) {
        NotificationManager.error(JSON.parse(e.response.data).message);
      }
    };

    const handleSummaryChange = (value) => {
      setSummary(value);
    };
    const handleTitleChange = (event) => {
      setTitle(event.target.value);
    };

    useAsyncEffect(async () => {
      if (props.item) {
        try {
          const client = await WatsonApi();
          const result =
            await client.apis.organizations.organizations_terraces_accordion_items_read(
              {
                organization_pk: organizationStore.currentOrganization.id,
                terrace_pk: (props.item as IAccordionItem).terrace_id,
                id: (props.item as IAccordionItem).id,
                expand: props.item.has_content ? ["content_html"] : [],
                raw: true,
              },
            );
          const accordionItem = JSON.parse(result.data);
          setSummary(accordionItem.description);
          setContent(accordionItem.content_html);
        } catch (e) {
          NotificationManager.error(e.response?.body?.detail);
        }
      }
    }, []);

    useEffect(() => {
      interfaceStore.scrollToElement(`#${htmlId}`);
    }, []);

    return (
      <div id={htmlId}>
        <div className={editStyles.inputGroup}>
          <label>Title</label>
          <div>
            <RequiredInput
              title="Message Title"
              className="form-control"
              type="text"
              onChange={handleTitleChange}
              value={title}
              valid={!!titleIsValid}
            />
          </div>
        </div>
        <div className={editStyles.inputGroup}>
          <label>{hasDocmersion ? "Summary (Optional)" : "Text"}</label>
          {typeof summary === "string" && (
            <MarkdownEditor
              handleTextChange={handleSummaryChange}
              value={summary || ""}
              editorClassName={styles.markdownEditor}
            />
          )}
        </div>
        {addContent && hasDocmersion ? (
          <div className={editStyles.inputGroup}>
            <label>
              Page Content{" "}
              <span
                className={styles.tooltip}
                data-content={
                  "Content will only be shown\nin the Accordion Item Modal\n" +
                  "and on the Accordion Item Page.\n" +
                  "Navigate to these locations respectively\n by clicking 'View More'\n" +
                  "on an Accordion Item with content, \n" +
                  "and by copying the page link from\n the 'Share' button in the modal."
                }
                style={{ marginLeft: ".25rem" }}
              >
                <FontAwesome prefix={"fas"} name={"fa-question-circle"} />
              </span>{" "}
              (Optional)
            </label>
            {props.item?.has_content && !content ? (
              <div>
                <SchoolBlocksLoadingIndicator />
              </div>
            ) : (
              <HtmlEditor
                value={content || ""}
                ref={htmlEditorRef}
                defaultEditor={"wysiwyg"}
                editorClassName={markdownEditorClassName}
              />
            )}
          </div>
        ) : (
          <div>
            <div>
              {!hasDocmersion && (
                <div className={styles.docmersionInfo}>
                  <ContactSupport
                    message={
                      "Adding content requires a Docmersion Subscription."
                    }
                    className={styles.docmersionText}
                  />
                  <div>
                    <ClickableLink
                      href={
                        "https://app.arcade.software/share/Q0aiaL3qDKFfQe9FboXV"
                      }
                    >
                      What is DocMersion?
                    </ClickableLink>
                  </div>
                </div>
              )}
              <div className={styles.editorButtons}>
                <button
                  disabled={!hasDocmersion}
                  className={`${styles.controlButton} ${styles.saveButton} btn`}
                  onClick={() => setAddContent(true)}
                >
                  Add Content <NewTag createdAt={"6-13-25"} />
                </button>
              </div>
            </div>
          </div>
        )}
        <div className={styles.editorButtons}>
          <button
            type="button"
            className={`${styles.controlButton} ${styles.saveButton} btn ${!titleIsValid ? styles.disabled : ""}`}
            disabled={!titleIsValid || current.value === "PENDING"}
            onClick={handleSubmit}
          >
            {current.value === "PENDING" ? (
              <>
                <FontAwesome prefix={"fas"} name={"fa-spinner"} spin />{" "}
                Saving...
              </>
            ) : (
              "Save Accordion Item"
            )}
          </button>
          <button
            type="button"
            className={`${styles.controlButton} ${styles.closeButton} btn`}
            disabled={false}
            onClick={props.handleCancel}
          >
            Cancel
          </button>
          {props.accordionList && props.item && (
            <button
              type="button"
              className={`${styles.controlButton} ${styles.deleteButton} btn`}
              disabled={false}
              onClick={handleDelete}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    );
  },
);

export default AccordionItemEditor;
